import React, { memo, useEffect, useMemo } from 'react';
import { useProposalBlockContext, useProposalContext, useProposalDataContext } from '@modules/proposal/ProposalBuilderPage';
import { formArray, formControl, FormDataType, formGroup } from '@core/utils/form';
import { VccEstimateGroup, VccEstimateGroupHeader } from './VccEstimateGroup';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { openBlockSettingsOverlay } from '../Common/BlockBaseConfig';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { useConfigureBlock } from '@modules/proposal/hooks';
import { FormHTMLInline } from '@core/components/form';
import { EstimateService, ProposalService } from '@business/services';
import { PricingSettings } from './PricingSettings';
import { BlockContainer } from '../BlockContainer';
import { PricingFooter } from './PricingFooter';
import classNames from 'classnames';
import { Proposal } from '../types';
import { defineSubProjectsForm, defineListItemsForm } from '@modules/proposals/AddEditProposalPage/AddEditProposalForm';
import { ProposalStatus } from '@business/entities/proposal';
import { defineProposalDiscountForm } from '@modules/proposal/ProposalBuilderPage/Components/ProposalProductsOverlay';
import { SubProjectUpgradeOptions } from './SubProjectUpgradeOptions';
import './index.scss';

export enum PricingBlockVariation {
  pricing = 'pricing',
}

function definePricingStaticRowsForm() {
  return formGroup({
    sectionTitle: formControl<string>({
      placeholder: 'Section Title',
      initialValue: 'Section Title',
    }),
  });
}

export function definePricingBlockForm() {
  return formGroup({
    title: formControl<string>({
      initialValue: 'Estimate Title',
    }),
    optionalItemsTitle: formControl<string>({
      label: 'Optional Items Title',
      initialValue: 'Upgrades & Options',
    }),
    sections: formArray(() => definePricingStaticRowsForm()),
    advance: defineBlockAdvanceForm(),
  });
}

export type TodoListFormData = FormDataType<typeof definePricingBlockForm>;
export type TodoListForm = ReturnType<typeof definePricingBlockForm>;

export const PricingBlock = memo((props: Proposal.BlockProps<PricingBlockVariation>) => {
  const { variation, preview, editable } = props;
  const proposalData = useProposalDataContext();

  const proposal = useProposalContext();
  const estimate = proposal?.estimate;

  const estimateGroupsItems = useMemo(() => estimate?.groups ?? [], [estimate]);
  const estimateGroups = useMemo(
    () => estimateGroupsItems.filter(group => group.items.filter(o => !o.weProvide && EstimateService.getMaterialPrice(o) > 0).length > 0),
    [estimateGroupsItems],
  );

  const form = useMemo(() => {
    if (proposal) return proposal.form;
    return formGroup({
      subProjects: formArray(() => defineSubProjectsForm()),
      listItems: formArray(() => defineListItemsForm()),
      pricing: definePricingBlockForm(),
      productsAdvance: defineBlockAdvanceForm(),
      discount: defineProposalDiscountForm(),
    });
  }, [proposal]);

  const formValue = useNonNilObservable(form.value$);
  const formSectionItems = useNonNilObservable(form.controls.pricing.controls.sections.items$);

  const subProjectItems = useNonNilObservable(form.controls.subProjects.items$);
  const subProjectItemValue = useNonNilObservable(form.controls.subProjects.value$);

  const listItemItems = useNonNilObservable(form.controls.listItems.items$);
  const listItemValues = useNonNilObservable(form.controls.listItems.value$);

  useEffect(() => {
    if (!formSectionItems.length) {
      form.controls.pricing.controls.sections.push();
    }
  }, [form.controls.pricing.controls.sections]);

  const primaryColor = useMemo(() => proposal?.form.value.primaryColor, [proposal]);

  const proposalStatus = useMemo(() => proposalData?.status, [proposalData]);
  const canCheckProducts = useMemo(() => {
    if (!proposalStatus) return true;
    return proposalStatus !== ProposalStatus.accepted;
  }, [proposalStatus]);

  // The pricing block's form is the whole proposal, so its style settings are the `pricing.advance` group. The drawer
  // is opened here with that group for its Settings tab (a Text Color change then only touches the pricing titles).
  const { blockForm } = useProposalBlockContext()!;
  useConfigureBlock({
    props,
    form,
    onEdit: () =>
      openBlockSettingsOverlay({
        advanceForm: form.controls.pricing.controls.advance,
        contentComponent: PricingSettings,
        props: { form: form, variation },
        blockForm,
      }),
  });

  const estimateTotal = useMemo(() => (estimate ? EstimateService.getEstimateTotal(estimate) : 0), [estimate]);
  const vccTotal = useMemo(() => (estimate ? EstimateService.getVccTotal(estimate) : 0), [estimate]);

  const selectedItemTotal = useMemo(
    () => subProjectItemValue.reduce((total, item) => total + (item.isChecked ? item.total : 0), 0),
    [subProjectItemValue],
  );

  // Billed portion (backend money math: labor + material-if-weProvide, markup applied) — goes
  // into the main total, matching what the work order will charge (TmListItem::total)
  const listItemsFixedPriceTotal = useMemo(
    () => listItemValues.reduce((acc, item) => acc + (item.isChecked ? EstimateService.getListItemAmounts(item).fixed : 0), 0),
    [listItemValues],
  );

  // VCC portion (material of NOT-we-provide items, markup applied) — VCC display line only
  const listItemsVccTotal = useMemo(
    () => listItemValues.reduce((acc, item) => acc + (item.isChecked ? EstimateService.getListItemAmounts(item).vcc : 0), 0),
    [listItemValues],
  );

  const total = useMemo(
    () => estimateTotal + vccTotal + selectedItemTotal + listItemsFixedPriceTotal,
    [estimateTotal, vccTotal, selectedItemTotal, listItemsFixedPriceTotal],
  );
  // Recalculated when the customer ticks upgrades too (a percent discount follows the total)
  const discount = useMemo(() => EstimateService.getDiscount(total, formValue.discount), [total, formValue.discount]);
  const gTotal = useMemo(() => total - discount, [total, discount]);
  const discountTitle = formValue.discount?.description?.trim() ? `Discount (${formValue.discount.description.trim()})` : 'Discount';

  const showUpgradesSection = subProjectItemValue.length > 0 || listItemItems.length > 0;

  return (
    <BlockContainer
      advanceForm={formValue.pricing.advance}
      className={classNames('d-flex flex-fill', 'block-pricing', variation, { 'has-text-color': !!formValue.pricing.advance?.color })}
      isSection={props.isSection}
      sectionTitle={props.sectionTitle}
      fullWidth={false}
      editable={editable}
    >
      {formSectionItems.map(sec => (
        <div className="proposal-table-header">
          <FormHTMLInline
            control={sec.controls.sectionTitle}
            readonly={!editable}
            transformedFieldsValue={ProposalService.getTransformedValues(proposalData)}
          />
        </div>
      ))}
      {/* vccs */}
      {estimateGroups.map(group => (
        <VccEstimateGroup group={group} primaryColor={primaryColor} />
      ))}
      {!!(estimateGroups.length == 0) && <VccEstimateGroupHeader />}
      <PricingFooter withBorder title="VCC Total" total={vccTotal + listItemsVccTotal} />
      {/* end vccs */}
      {/* upgrades and options + list items */}
      {showUpgradesSection && (
        <SubProjectUpgradeOptions
          optionalItemsTitle={formValue.pricing.optionalItemsTitle}
          subProjectItems={subProjectItems}
          listItemItems={listItemItems}
          primaryColor={primaryColor}
          selectedItemTotal={selectedItemTotal}
          listItemsFixedPriceTotal={listItemsFixedPriceTotal}
          canCheckProducts={canCheckProducts}
        />
      )}
      {/* end upgrades and options */}
      {discount != 0 && <PricingFooter title="Subtotal" total={total} />}
      {discount != 0 && <PricingFooter title={discountTitle} total={discount} />}
      <PricingFooter title="Total Estimated Cost" total={gTotal} />
    </BlockContainer>
  );
});
